/**
 * Noor authentication and profile fetching
 */
import { saveAuthData, saveProfileData, getAuthData } from './noor-db.js';
import NoorConfig from './noor-config.js';

const API_BASE_URL = NoorConfig.api.baseUrl;

/**
 * Authenticate with FastAPI
 */
export async function authenticateWithFastAPI(username, password) {
    console.log('🔵 FastAPI Auth: Starting authentication for:', username);
    try {
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        const response = await fetch(`${API_BASE_URL}${NoorConfig.api.endpoints.login}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Login failed with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ FastAPI authentication successful');
        console.log('📦 Auth data received:', { username: data.user?.username, roles: data.user?.user_role });
        
        // Save to IndexedDB
        console.log('💾 Saving auth data to IndexedDB...');
        await saveAuthData(data);
        console.log('✅ Auth data saved to IndexedDB');
        
        return data;
    } catch (authError) {
        console.error('FastAPI authentication error:', authError);
        throw authError;
    }
}

/**
 * Fetch user profile from API
 */
export async function fetchUserProfile(username, accessToken) {
    console.log('🔵 Profile Fetch: Starting for:', username);
    try {
        // Check if crypto functions are available
        if (typeof window.data_encryptor !== 'function') {
            console.error('❌ Crypto library not loaded!');
            throw new Error('Crypto library not loaded');
        }
        
        if (typeof window.md5hasher !== 'function' || typeof window.aes_decrypt !== 'function') {
            throw new Error('Crypto utilities not loaded');
        }

        // Encrypt the request data
        const requestData = { key: username };
        const encrypted = await window.data_encryptor(requestData, username);

        // Prepare form data for API
        const formData = new FormData();
        formData.append('data', encrypted.data);
        formData.append('key', encrypted.key);

        // Fetch profile from API
        const response = await fetch(`${API_BASE_URL}${NoorConfig.api.endpoints.profile}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Profile fetch failed with status: ${response.status}`);
        }

        const encryptedResponse = await response.json();
        console.log('Encrypted profile response received');
        console.log('📦 Response structure:', {
            hasIV: !!encryptedResponse.iv,
            hasCiphertext: !!encryptedResponse.ciphertext,
            ivLength: encryptedResponse.iv?.length,
            ciphertextLength: encryptedResponse.ciphertext?.length
        });

        // Decrypt the response
        console.log('🔐 Step 2A: Preparing decryption key...');
        let keyHex, keyBytes, keyBase64;
        try {
            keyHex = window.md5hasher(username);
            console.log('✅ MD5 hash generated (hex string):', keyHex);
        } catch (md5Error) {
            console.error('❌ MD5 hash failed:', md5Error);
            throw new Error(`MD5 hash failed: ${md5Error.message}`);
        }
        
        try {
            // Backend does: md5hasher(username).encode() which encodes the HEX STRING as UTF-8
            // So we need to encode the hex string as UTF-8, NOT convert hex to bytes
            const encoder = new TextEncoder();
            keyBytes = encoder.encode(keyHex);
            console.log('✅ Hex string encoded as UTF-8, length:', keyBytes?.length, 'bytes');
        } catch (encodeError) {
            console.error('❌ UTF-8 encoding failed:', encodeError);
            throw new Error(`UTF-8 encoding failed: ${encodeError.message}`);
        }
        
        try {
            keyBase64 = btoa(String.fromCharCode(...keyBytes));
            console.log('✅ Key prepared as base64, length:', keyBase64?.length);
        } catch (base64Error) {
            console.error('❌ Base64 encoding failed:', base64Error);
            throw new Error(`Base64 encoding failed: ${base64Error.message}`);
        }

        console.log('🔐 Step 2B: Decrypting profile data...');
        console.log('Calling aes_decrypt with:', {
            keyLength: keyBase64.length,
            ivLength: encryptedResponse.iv.length,
            ciphertextLength: encryptedResponse.ciphertext.length
        });
        
        let decryptedJson;
        try {
            decryptedJson = await window.aes_decrypt(
                keyBase64,
                encryptedResponse.iv,
                encryptedResponse.ciphertext
            );
            console.log('✅ Decryption successful, result length:', decryptedJson?.length);
        } catch (decryptError) {
            console.error('❌ AES decryption failed:', decryptError);
            console.error('Decryption error details:', {
                name: decryptError.name,
                message: decryptError.message,
                code: decryptError.code
            });
            throw new Error(`AES decryption failed: ${decryptError.message}`);
        }

        const profileData = JSON.parse(decryptedJson);
        console.log('✅ Profile data decrypted:', profileData);
        console.log('📦 Subjects from profile:', profileData.subjects);

        // Save to IndexedDB
        console.log('💾 Saving profile to IndexedDB...');
        await saveProfileData(profileData);
        console.log('✅ Profile saved to IndexedDB');
        console.log('📊 Profile stats: subjects:', profileData.subjects?.length || 0);

        return profileData;
    } catch (profileError) {
        console.error('❌ Failed to fetch user profile:', profileError);
        console.error('Error type:', profileError.constructor.name);
        console.error('Error message:', profileError.message);
        throw profileError;
    }
}

/**
 * Complete login flow: authenticate + fetch profile
 */
export async function loginWithNoor(username, password) {
    console.log('🔵 Login with Noor: Starting complete login flow for:', username);
    try {
        // Authenticate
        console.log('📡 Step 1: Authenticating with FastAPI...');
        const authData = await authenticateWithFastAPI(username, password);
        console.log('📋 User roles:', authData.user?.user_role);
        console.log('✅ Step 1 complete: Authentication successful');
        
        // Fetch profile
        console.log('📡 Step 2: Fetching user profile...');
        try {
            await fetchUserProfile(username, authData.access_token);
            console.log('✅ Step 2 complete: User profile loaded successfully');
        } catch (profileFetchError) {
            console.error('⚠️ Step 2 FAILED: Failed to fetch profile (continuing login):', profileFetchError);
            console.error('Profile fetch error details:', profileFetchError.message);
            if (profileFetchError.stack) {
                console.error('Stack trace:', profileFetchError.stack);
            }
        }
        
        console.log('✅ Login with Noor: Complete login flow finished');
        return authData;
    } catch (loginError) {
        console.error('❌ Failed to login with Noor:', loginError);
        throw loginError;
    }
}

/**
 * Get stored access token
 */
export async function getAccessToken() {
    try {
        const authData = await getAuthData();
        return authData.accessToken || '';
    } catch (tokenError) {
        console.error('Failed to get access token:', tokenError);
        return '';
    }
}
